import React from "react";
import { useSelector } from "react-redux";
import SummaryWrapper from "./SummaryWrapper.style";
import AttributesList from "../../../components/layouts/AttributesList";
import moment from "moment";
import { RootState } from "../types";

/**
 * Converts minutes to hours with rounding to 2 decimal places
 *
 * @param minutes - The number of minutes to convert
 * @returns The converted hours value or undefined if input is undefined
 */
const convertMinutesToHours = (minutes?: number): number | undefined =>
  minutes !== undefined ? Math.round((minutes / 60) * 100) / 100 : undefined;

/**
 * Constants for attribute translation keys used in the summary display
 */
const ATTRIBUTE_KEYS = {
  APPROVED_MINUTES: "app.salary.approved_minutes",
  RETRO_MINUTES: "app.salary.retro_minutes",
  UNAPPROVED_MINUTES: "app.salary.unapproved_minutes",
  SALARY_RUN_AT: "app.salary.salary_run_at",
  TOTAL_EMPLOYEES: "app.salary.total_employees",
} as const;

type AttributeKey = (typeof ATTRIBUTE_KEYS)[keyof typeof ATTRIBUTE_KEYS];
type AttributesMap = Partial<Record<AttributeKey, number | string>>;

/**
 * Summary component for the Salary module
 *
 * Displays key metrics about the salary period including:
 * - Approved hours (converted from minutes)
 * - Retro hours (converted from minutes)
 * - Unapproved hours (converted from minutes)
 * - When salary calculations were last run
 * - Total number of employees in the current selection
 *
 * The component retrieves data from the Redux store and formats
 * it for display using the AttributesList component.
 *
 * @returns React component displaying summary information about salaries
 */
const Summary: React.FC = () => {
  // Retrieve summary data and total employee count from Redux store
  const summary = useSelector((state: RootState) => state.salary.page.summary);
  const total = useSelector((state: RootState) => state.salary.page.total);

  // Destructure with default empty object to prevent undefined errors
  const { approved_minutes, retro_minutes, unapproved_minutes, salary_run_at } =
    summary || {};

  // Build attributes object with only defined values
  const attributes: AttributesMap = Object.entries({
    [ATTRIBUTE_KEYS.APPROVED_MINUTES]: convertMinutesToHours(approved_minutes),
    [ATTRIBUTE_KEYS.RETRO_MINUTES]: convertMinutesToHours(retro_minutes),
    [ATTRIBUTE_KEYS.UNAPPROVED_MINUTES]:
      convertMinutesToHours(unapproved_minutes),
    [ATTRIBUTE_KEYS.SALARY_RUN_AT]: salary_run_at
      ? moment(salary_run_at).format("HH:mm DD/MM/YYYY")
      : undefined,
    [ATTRIBUTE_KEYS.TOTAL_EMPLOYEES]: total,
  }).reduce<AttributesMap>((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key as AttributeKey] = value;
    }
    return acc;
  }, {});

  return (
    <SummaryWrapper>
      <AttributesList attributes={attributes} />
    </SummaryWrapper>
  );
};

export default Summary;
